import { Box, Paper } from '@mui/material';
import { useLocation, matchPath } from 'react-router-dom';

import Header from 'modules/common/components/Layout/Header';
import { useIMCContext } from 'modules/IMC/common/context';
import { RIGHT_DRAWER_WIDTH } from 'modules/common/components/RightDrawer/Drawer';
import { LEFT_DRAWER_WIDTH } from 'modules/common/components/Layout/Sidebar/common/Drawer';
import LaunchpadTasks from 'modules/common/components/Layout/LaunchpadTasks';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import { useMember } from 'modules/common/queries/member';
import { isInOnboardingStages } from 'modules/common/utils/memberStage';

export default function Content({ children }: { children: React.ReactNode }) {
    const { currOpenDrawer } = useIMCContext();
    const { data: member } = useMember();
    const location = useLocation();
    const isDashboard = matchPath(location.pathname, '/dashboard') !== null;
    const { isLaunchpadVisible } = useLayoutContext();
    const launchpadVisible = isLaunchpadVisible && isDashboard && isInOnboardingStages(member?.stage);

    return (
        <Box
            component="main"
            sx={{
                position: 'relative',
                flexGrow: 1,
                backgroundColor: 'cement.50',
                paddingRight: {
                    xs: 0,
                    md: 1,
                },
                width: `calc(100% - ${LEFT_DRAWER_WIDTH}px)`,
                transition: 'all 0.4s ease',
                ...(currOpenDrawer !== null && {
                    width: `calc(100% - ${LEFT_DRAWER_WIDTH}px - ${RIGHT_DRAWER_WIDTH + 10}px)`,
                }),
            }}>
            <Paper
                sx={{
                    marginTop: { xs: 0, md: 1 },
                    height: {
                        xs: '100%',
                        md: 'calc(100% - 16px)',
                    },
                    paddingBottom: {
                        md: 5,
                    },
                    backgroundColor: launchpadVisible ? 'transparent' : 'white',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    borderRadius: 3,
                    scrollbarGutter: 'stable',
                    '&::-webkit-scrollbar': {
                        width: 4,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'grey.100',
                        borderRadius: 3,
                    },
                }}
                elevation={0}>
                <Header />
                {isDashboard && isInOnboardingStages(member?.stage) && <LaunchpadTasks />}
                <Box
                    aria-label="Page Content"
                    sx={{
                        display: 'flex',
                        backgroundColor: 'white',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingInline: {
                            xs: 3,
                            md: 4,
                        },
                        paddingBlock: {
                            xs: 2,
                            md: 4,
                        },
                        maxWidth: {
                            xs: '100dvw',
                            xl: '1800px',
                        }, // for 2k resolutions and up
                        width: '100%',
                        ...(launchpadVisible && {
                            borderTopRightRadius: 12,
                            borderTopLeftRadius: 12,
                        }),
                    }}>
                    {children}
                </Box>
            </Paper>
        </Box>
    );
}
